import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type PracticeReview, type PracticeSession as Session } from '../lib/api';

export function PracticeSession() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [review, setReview] = useState<PracticeReview | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState<Set<string>>(new Set());
  const [remaining, setRemaining] = useState<number | null>(null); // seconds (test-series)
  const submittedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .getSession(String(id))
      .then((s) => {
        if (cancelled) return;
        if (s.status === 'completed') setReview(s);
        else setSession(s);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    api.getBookmarks().then((r) => !cancelled && setSaved(new Set(r.ids))).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [id]);

  const inProgress = session && session.status === 'in_progress' ? session : null;
  const questions = inProgress ? inProgress.questions : [];
  const answered = Object.keys(answers).length;

  const doSubmit = useCallback(async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const r = await api.submitSession(String(id), answers);
      setReview(r);
      window.scrollTo(0, 0);
    } catch {
      submittedRef.current = false;
      setSubmitting(false);
      alert('Could not submit. Please try again.');
    }
  }, [id, answers]);

  // Countdown for any timed session (test-series always; quiz/pyq if the
  // product sets a duration). Server-anchored so a reload doesn't reset it.
  useEffect(() => {
    if (!inProgress || !inProgress.startedAt || !inProgress.durationMin) return;
    const endsAt = new Date(inProgress.startedAt).getTime() + inProgress.durationMin * 60000;
    const tick = () => {
      const secs = Math.max(0, Math.round((endsAt - Date.now()) / 1000));
      setRemaining(secs);
      if (secs <= 0) doSubmit();
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [inProgress, doSubmit]);

  async function toggleBookmark(qid: string) {
    try {
      const { bookmarked } = await api.toggleBookmark(qid);
      setSaved((prev) => {
        const next = new Set(prev);
        if (bookmarked) next.add(qid);
        else next.delete(qid);
        return next;
      });
    } catch {
      /* ignore */
    }
  }

  function confirmSubmit() {
    const unanswered = questions.length - answered;
    if (window.confirm(unanswered > 0 ? `${unanswered} question(s) unanswered. Submit anyway?` : 'Submit your answers?')) doSubmit();
  }

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (review) return <Results review={review} onDone={() => navigate(-1)} saved={saved} onToggle={toggleBookmark} />;

  if (!inProgress || questions.length === 0) {
    return (
      <div className="col center" style={{ padding: 60, gap: 10 }}>
        <div style={{ fontSize: 40 }}>😕</div>
        <div style={{ fontWeight: 800 }}>Session unavailable</div>
        <button className="btn btn-primary" onClick={() => navigate('/')}>Home</button>
      </div>
    );
  }

  const q = questions[current];
  const total = questions.length;
  const lowTime = remaining !== null && remaining < 60;

  return (
    <div style={{ paddingTop: 16, maxWidth: 720, margin: '0 auto' }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <button className="link" style={{ background: 'none', border: 'none' }} onClick={() => { if (window.confirm('Leave? Progress will be lost.')) navigate(-1); }}>✕ Close</button>
        <div className="row gap8">
          {remaining !== null && (
            <span className="chip" style={{ background: lowTime ? 'var(--danger-soft)' : 'var(--primary-soft)', color: lowTime ? 'var(--danger)' : 'var(--primary)', border: 'none', fontWeight: 800 }}>
              ⏱️ {Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, '0')}
            </span>
          )}
          <span className="chip active">{answered}/{total}</span>
        </div>
      </div>

      <div style={{ margin: '14px 0 6px' }}>
        <div style={{ height: 7, borderRadius: 4, background: 'var(--divider)', overflow: 'hidden' }}>
          <div style={{ width: `${((current + 1) / total) * 100}%`, height: '100%', background: 'var(--primary)' }} />
        </div>
        <div className="muted" style={{ fontSize: 12, fontWeight: 700, marginTop: 6 }}>Question {current + 1} of {total}</div>
      </div>

      <div className="row" style={{ alignItems: 'flex-start', gap: 10, marginTop: 14 }}>
        <div style={{ flex: 1 }}>
          {q.year && <span className="badge" style={{ background: 'var(--primary-soft)', color: 'var(--primary)', marginBottom: 6 }}>{q.year}{q.session ? ` · ${q.session}` : ''}</span>}
          <h2 style={{ fontSize: 19, fontWeight: 800, lineHeight: 1.4 }}>{q.text}</h2>
        </div>
        <button onClick={() => toggleBookmark(q.id)} title="Bookmark" style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', opacity: saved.has(q.id) ? 1 : 0.35, lineHeight: 1 }}>🔖</button>
      </div>

      <div className="col gap12" style={{ marginTop: 20 }}>
        {q.options.map((opt, i) => {
          const selected = answers[q.id] === i;
          return (
            <button
              key={i}
              onClick={() => setAnswers((a) => ({ ...a, [q.id]: i }))}
              className="card row gap12"
              style={{ textAlign: 'left', border: selected ? '1.5px solid var(--primary)' : '1.5px solid var(--border)', background: selected ? 'var(--primary-soft)' : 'var(--surface)' }}
            >
              <span className="center" style={{ width: 32, height: 32, borderRadius: 16, background: selected ? 'var(--primary)' : 'var(--divider)', color: selected ? '#fff' : 'var(--muted)', fontWeight: 800 }}>{String.fromCharCode(65 + i)}</span>
              <span style={{ flex: 1, color: selected ? 'var(--primary)' : 'var(--text)', fontWeight: selected ? 700 : 400 }}>{opt}</span>
              {selected && <span style={{ color: 'var(--primary)' }}>✓</span>}
            </button>
          );
        })}
      </div>

      <div className="row gap12" style={{ marginTop: 24 }}>
        <button className="btn btn-outline" style={{ flex: 1 }} disabled={current === 0} onClick={() => setCurrent((c) => Math.max(0, c - 1))}>Previous</button>
        {current === total - 1 ? (
          <button className="btn btn-primary" style={{ flex: 1.4 }} onClick={confirmSubmit} disabled={submitting}>{submitting ? 'Submitting…' : 'Submit'}</button>
        ) : (
          <button className="btn btn-primary" style={{ flex: 1.4 }} onClick={() => setCurrent((c) => Math.min(total - 1, c + 1))}>Next →</button>
        )}
      </div>
    </div>
  );
}

function Results({
  review,
  onDone,
  saved,
  onToggle,
}: {
  review: PracticeReview;
  onDone: () => void;
  saved: Set<string>;
  onToggle: (id: string) => void;
}) {
  const isTs = review.type === 'testseries';
  const passed = isTs ? (review.marks ?? 0) >= (review.maxMarks ?? 0) * 0.5 : review.percent >= 60;

  return (
    <div style={{ paddingTop: 16, maxWidth: 720, margin: '0 auto' }}>
      <div className="card col center" style={{ background: passed ? 'var(--success-soft)' : 'var(--accent-soft)', padding: 28, textAlign: 'center' }}>
        <div style={{ fontSize: 52 }}>{passed ? '🏆' : '💪'}</div>
        <h1 style={{ fontSize: 22, fontWeight: 900, marginTop: 8 }}>{passed ? 'Well done!' : 'Keep practising!'}</h1>
        {isTs ? (
          <>
            <div style={{ fontSize: 42, fontWeight: 900, marginTop: 8 }}>{review.marks} / {review.maxMarks}</div>
            <div className="muted" style={{ fontWeight: 700 }}>marks · {review.score}/{review.total} correct</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Marking +{review.marksCorrect}/{review.marksWrong}</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 42, fontWeight: 900, marginTop: 8 }}>{review.score} / {review.total}</div>
            <div className="muted" style={{ fontWeight: 700 }}>{review.percent}% correct</div>
          </>
        )}
      </div>

      <div className="row gap12" style={{ marginTop: 16 }}>
        <Stat label="Correct" value={review.correct} color="var(--success)" />
        <Stat label="Wrong" value={review.wrong} color="var(--danger)" />
        <Stat label="Skipped" value={review.skipped} color="var(--muted)" />
      </div>

      <h2 className="section-title" style={{ margin: '24px 0 12px' }}>Solutions</h2>
      {review.questions.map((q, i) => (
        <div key={q.id} className="card" style={{ marginBottom: 12 }}>
          <div className="row gap8" style={{ alignItems: 'flex-start' }}>
            <span>{q.correct ? '✅' : q.your === null ? '⚪' : '❌'}</span>
            <div style={{ fontWeight: 700, lineHeight: 1.4, flex: 1 }}>
              {q.year && <span className="badge" style={{ background: 'var(--primary-soft)', color: 'var(--primary)', marginRight: 6 }}>{q.year}{q.session ? ` · ${q.session}` : ''}</span>}
              {i + 1}. {q.text}
            </div>
            <button onClick={() => onToggle(q.id)} title="Bookmark" style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', opacity: saved.has(q.id) ? 1 : 0.35 }}>🔖</button>
          </div>
          <div className="col gap8" style={{ marginTop: 10 }}>
            {q.options.map((opt, oi) => {
              const isCorrect = oi === q.answer;
              const isYour = oi === q.your;
              return (
                <div key={oi} className="row" style={{ justifyContent: 'space-between', padding: '8px 10px', borderRadius: 8, background: isCorrect ? 'var(--success-soft)' : isYour ? 'var(--danger-soft)' : 'var(--surface-alt)' }}>
                  <span style={{ fontWeight: isCorrect || isYour ? 700 : 400, color: isCorrect || isYour ? 'var(--text)' : 'var(--muted)' }}>{String.fromCharCode(65 + oi)}. {opt}</span>
                  {isCorrect && <span style={{ color: 'var(--success)' }}>✓</span>}
                  {isYour && !isCorrect && <span style={{ color: 'var(--danger)' }}>✕</span>}
                </div>
              );
            })}
          </div>
          {q.solution && <p className="muted" style={{ marginTop: 10, lineHeight: 1.5 }}><b>Solution: </b>{q.solution}</p>}
        </div>
      ))}

      <button className="btn btn-primary btn-block" style={{ marginTop: 8 }} onClick={onDone}>Done</button>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="card center col" style={{ flex: 1, padding: 16 }}>
      <div style={{ fontSize: 24, fontWeight: 900, color }}>{value}</div>
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
    </div>
  );
}
